import type { MDXMetadata } from 'mdx/types'
import type { AnchorHTMLAttributes, ReactNode } from 'react'
import {
  cloneElement,
  createContext,
  createElement,
  isValidElement,
  use,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from 'react'
import { parse } from 'regexparam'
import { registry } from '#course-catalog'

export interface RouteParams {
  [key: string]: string | undefined
}

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string
  replace?: boolean
  ref?: React.Ref<HTMLAnchorElement>
}

export interface RouteProps {
  path?: string
  children?: ReactNode | React.ComponentType
}

export interface SwitchProps {
  pathname?: string
  children?: ReactNode
}

export interface RouterProps {
  initialHref?: string
  children: ReactNode
}

export interface NavigateOptions {
  replace?: boolean
}

function matchPath(
  pattern: string | undefined,
  pathname: string,
): [boolean, RouteParams | null] {
  if (!pattern) return [true, {}]

  const { keys, pattern: regex } = parse(pattern)
  const match = regex.exec(pathname)
  if (!match) return [false, null]

  const params = keys.reduce<RouteParams>((acc, key, i) => {
    acc[key] = match[i + 1]
    return acc
  }, {})
  return [true, params]
}

type CourseMetadata = MDXMetadata & { type: 'course' }
const isCourse = (i?: MDXMetadata): i is CourseMetadata => i?.type === 'course'

function getCourse(i?: MDXMetadata): CourseMetadata | undefined {
  if (!i) return undefined
  if (isCourse(i)) return i
  return getCourse(i?.parent)
}

interface LocationContextValue extends URL {
  navigate: (to: string, options?: NavigateOptions) => void
  isPending: boolean
  page?: MDXMetadata
  course?: CourseMetadata
}

const LocationContext = createContext<LocationContextValue | null>(null)
const ParamsContext = createContext<RouteParams>({})

export const useLocation = () => use(LocationContext)!
export const useParams = () => use(ParamsContext)
export function useRoute<TParams extends RouteParams = RouteParams>(
  pattern?: string,
): [boolean, TParams] {
  const { pathname } = useLocation()
  const [matches, params] = matchPath(pattern, pathname)
  return [matches, (params ?? {}) as TParams]
}

export function Router({ children, initialHref }: RouterProps) {
  const [isPending, startTransition] = useTransition()
  const [href, setHref] = useState(() => {
    if (initialHref) return initialHref
    if (typeof window !== 'undefined') return window.location.href
    return '/'
  })

  // Scroll restoration state
  const scrollPositionsRef = useRef(new Map<string, number>())
  const currentKeyRef = useRef<string | null>(null)
  const keyCounterRef = useRef(0)
  const pendingScrollRef = useRef<
    { type: 'TOP' } | { type: 'RESTORE'; key: string } | null
  >(null)

  // Initialize scroll restoration and ensure every history entry has a key
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return

    try {
      if ('scrollRestoration' in window.history) {
        // Disable browser auto restoration to prevent premature jumps
        window.history.scrollRestoration = 'manual'
      }
    } catch {
      // ignore
    }

    const state = window.history.state ?? {}
    let key: string | undefined = state?.key
    if (!key) {
      key = String(++keyCounterRef.current)
      window.history.replaceState({ ...state, key }, '', window.location.href)
    }
    currentKeyRef.current = key

    const handlePopState = (event: PopStateEvent) => {
      // Save current scroll before leaving this entry
      if (currentKeyRef.current != null) {
        scrollPositionsRef.current.set(currentKeyRef.current, window.scrollY)
      }

      // Determine the key for the target history entry
      const nextKey = event.state?.key || window.history.state?.key || null

      if (nextKey) {
        currentKeyRef.current = nextKey
        pendingScrollRef.current = { type: 'RESTORE', key: nextKey }
      } else {
        // If no key found, default to top after transition
        pendingScrollRef.current = { type: 'TOP' }
      }

      startTransition(() => setHref(window.location.href))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Navigation function that uses startTransition and manages scroll intent
  const navigate = (to: string, options?: NavigateOptions) => {
    if (typeof window === 'undefined') return
    const method = options?.replace ? 'replaceState' : 'pushState'

    startTransition(() => {
      // Save current scroll for current entry
      if (currentKeyRef.current != null) {
        scrollPositionsRef.current.set(currentKeyRef.current, window.scrollY)
      }

      // Create a new key for the next entry
      const nextKey = String(++keyCounterRef.current)
      const prevState = window.history.state ?? {}
      window.history[method]({ ...prevState, key: nextKey }, '', to)
      currentKeyRef.current = nextKey

      // On programmatic navigation, we scroll to top AFTER the transition completes
      pendingScrollRef.current = { type: 'TOP' }

      // TODO:
      // addTransitionType(
      //   'navigation',
      // )

      setHref(to)
    })
  }

  // After transition completes (including Suspense resolution), perform the intended scroll
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return
    if (isPending) return

    const pending = pendingScrollRef.current
    if (!pending) return

    if (pending.type === 'RESTORE') {
      const y = scrollPositionsRef.current.get(pending.key) ?? 0
      window.scrollTo(0, y)
    } else {
      // Percent-decode the fragment to match native anchor behavior
      // (#se%C3%A7%C3%A3o should find id="seção"); if decoding throws on
      // a malformed escape (e.g. "#100%"), fall back to the raw fragment.
      let fragment = window.location.hash.slice(1)
      try {
        fragment = decodeURIComponent(fragment)
      } catch {}
      const anchor = fragment ? document.getElementById(fragment) : null
      if (anchor) {
        anchor.scrollIntoView()
      } else {
        window.scrollTo(0, 0)
      }
    }

    pendingScrollRef.current = null
  }, [isPending])

  const base =
    typeof window !== 'undefined'
      ? window.location.href
      : (initialHref ?? 'http://localhost')

  const url = new URL(href, base)
  const page = registry.get(url.pathname)
  const course = getCourse(page)

  const location = Object.assign(url, {
    navigate,
    isPending,
    ...(page && { page }),
    ...(course && { course }),
  })

  return <LocationContext value={location}>{children}</LocationContext>
}

export function Route({ path, children }: RouteProps) {
  const [matches, params] = useRoute(path)
  if (!matches) return null

  return (
    <ParamsContext value={params}>
      {typeof children === 'function' ? createElement(children) : children}
    </ParamsContext>
  )
}

export function Link({
  href,
  onClick: userOnClick,
  replace,
  ref,
  ...restProps
}: LinkProps) {
  const { navigate } = useLocation()

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Ignore if modifier keys are pressed or not a left click
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return
    }

    userOnClick?.(event)

    if (!event.defaultPrevented) {
      event.preventDefault()
      navigate(href, { replace })
    }
  }

  return <a {...restProps} ref={ref} href={href} onClick={onClick} />
}

export function Switch(props: SwitchProps) {
  const { pathname } = useLocation()
  const children = Array.isArray(props.children)
    ? props.children
    : [props.children]

  for (const child of children) {
    if (!isValidElement(child)) continue
    const childProps = child.props as RouteProps
    const [match] = matchPath(childProps.path, props.pathname ?? pathname)
    if (match) return cloneElement(child, { ...childProps })
  }

  return null
}
